import { observer } from 'mobx-react';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { storesContext } from '../../../stores/storesContext';
import { errorToast, warningToast } from '../../helpers/toasts/ToastUtils';
import BlockInfoCard from '../BlockInfoCard';
import G2Monitoring from './g2_monitoring/G2Monitoring';
import G3Forecasting from './g3_forecasting/G3Forecasting';
import GenerationTimings from './generation_timings/GenerationTimings';
import PackhouseAssessment from './packhouse_assessment/PackhouseAssessment';
import RiskAssessment from './risk_assessment/RiskAssessment';

const PageContent = ({ monitoringBlock }) => {
  const userStore = useContext(storesContext);
  const alcmStore = userStore.alcmStore;
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!userStore.selectedDatabase) return;

    alcmStore.fetchG2Model(monitoringBlock.id).catch(() => {
      errorToast('Failed to load G2 monitoring data.');
    });
  }, [alcmStore, monitoringBlock.id, userStore.selectedDatabase]);

  useEffect(() => {
    const startDate = monitoringBlock.g2_trap_catch_peak_date;
    if (!startDate) {
      warningToast('This block has no G2 trap catch peak date.');
      alcmStore.resetG3Model();
      return;
    }

    const stopDate = moment(startDate)
      .add({ months: 6 })
      .format('YYYY-MM-DD HH:mm:ss');

    alcmStore.fetchG3Model(monitoringBlock, startDate, stopDate).catch(() => {
      errorToast('Failed to load G3 GDD and rainfall data.');
    });
  }, [alcmStore, monitoringBlock]);

  useEffect(() => {
    if (!userStore.selectedDatabase) return;

    alcmStore.fetchRiskAssessmentModel(monitoringBlock.id).catch(() => {
      errorToast('Failed to load risk assessment data.');
    });
  }, [alcmStore, monitoringBlock.id, userStore.selectedDatabase]);

  useEffect(() => {
    setInitialLoading(false);
  }, []);

  const loading = initialLoading || alcmStore.modelLoading;

  return (
    <div>
      <Row className='mb-4 justify-content-center'>
        <Col md={8} lg={6}>
          <BlockInfoCard monitoringBlock={monitoringBlock} />
        </Col>
      </Row>
      {loading ? (
        <div className='text-center py-4'>
          <Spinner animation='border' variant='primary' size='sm' />
          <p className='text-muted mt-2 mb-0'>Loading...</p>
        </div>
      ) : (
        <>
          <Row className='mb-4'>
            <Col xs={12} md={6} className='mb-4 mb-md-0'>
              <G2Monitoring
                monitoringBlock={monitoringBlock}
                g2Model={alcmStore.g2Model}
              />
            </Col>
            <Col xs={12} md={6}>
              <G3Forecasting
                monitoringBlock={monitoringBlock}
                g3Model={alcmStore.g3Model}
              />
            </Col>
          </Row>
          <Row className='mb-4'>
            <Col>
              <GenerationTimings
                monitoringBlock={monitoringBlock}
                g2Model={alcmStore.g2Model}
                g3Model={alcmStore.g3Model}
              />
            </Col>
          </Row>
          <Row>
            <Col md={9}>
              <RiskAssessment
                monitoringBlock={monitoringBlock}
                riskAssessmentData={alcmStore.riskAssessmentModel}
              />
            </Col>
            <Col md={3}>
              <PackhouseAssessment
                packhouseAssessmentData={
                  monitoringBlock.alcm_packhouse_assessment
                }
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default observer(PageContent);

